
import { initializeApp, deleteApp } from "firebase/app";
import { getAuth, createUserWithEmailAndPassword, signOut } from "firebase/auth";
import { doc, setDoc, getDocs, collection, query, where } from "firebase/firestore";
import { db, firebaseConfig } from "../firebase";

/**
 * Creates a Firebase Auth account using a secondary app instance (avoids signing out the current admin).
 * Does NOT write to Firestore. Silently ignores email-already-in-use (idempotent).
 */
export const createFirebaseAuthUser = async (email: string, password: string): Promise<{ uid: string }> => {
    const appName = `SecondaryApp-${Date.now()}`;
    const secondaryApp = initializeApp(firebaseConfig, appName);
    const secondaryAuth = getAuth(secondaryApp);
    try {
        const cred = await createUserWithEmailAndPassword(secondaryAuth, email, password);
        return { uid: cred.user.uid };
    } catch (error: any) {
        if (error.code === 'auth/email-already-in-use') return { uid: '' };
        throw new Error(error.message || 'Failed to create Firebase Auth user.');
    } finally {
        try { await signOut(secondaryAuth); await deleteApp(secondaryApp); } catch {}
    }
};

// Define simplified user types for registration
interface RegistrationData {
    email: string;
    role: 'student' | 'teacher' | 'non-academic' | 'staff'; // Added non-academic to be safe
    passwordPrefix?: string;
    [key: string]: any;
}

/**
 * Generates a default password in the format KCA-[Year]-[Role]
 * @param role 'student' | 'teacher' | 'staff'
 * @returns string
 */
export const generateDefaultPassword = (role: string, prefix: string = 'KCA'): string => {
    const year = new Date().getFullYear();
    // Capitalize the first letter of the role
    const formattedRole = role.charAt(0).toUpperCase() + role.slice(1);
    return `${prefix}-${year}-${formattedRole}`;
};

/**
 * Registers a user using a secondary Firebase app instance to avoid logging out the current admin.
 * @param collectionName 'students', 'teachers', or 'non_academic_staff'
 * @param userData The data to be saved to Firestore
 * @returns Promise<{ password: string, uid: string }>
 */
export const registerUser = async (
    collectionName: 'students' | 'teachers' | 'non_academic_staff',
    userData: RegistrationData
): Promise<{ password: string; uid: string }> => {

    // 1. Validate Email Uniqueness (Optional but recommended before creating Auth user)
    // We check the target collection first. Note: Auth email uniqueness is enforced by Firebase Auth itself,
    // but checking here allows us to return a clearer "User already exists in database" error.
    const usersRef = collection(db, collectionName);
    const q = query(usersRef, where("email", "==", userData.email));
    const querySnapshot = await getDocs(q);

    if (!querySnapshot.empty) {
        throw new Error(`A user with email ${userData.email} already exists in ${collectionName}.`);
    }

    // 2. Generate Password if not provided
    const passwordPrefix = userData.passwordPrefix || 'KCA';
    const password = userData.password || generateDefaultPassword(userData.role, passwordPrefix);

    // 3. Initialize Secondary App
    // We pass a unique name "Secondary" to avoid conflict with the default app
    // Using a timestamp to ensure uniqueness if rapid calls happen (though deleteApp should handle it)
    const secondaryAppName = `SecondaryApp-${Date.now()}`;
    const secondaryApp = initializeApp(firebaseConfig, secondaryAppName);
    const secondaryAuth = getAuth(secondaryApp);

    try {
        // 4. Create User in Secondary Auth
        const userCredential = await createUserWithEmailAndPassword(secondaryAuth, userData.email, password);
        const { uid } = userCredential.user;

        // 5. Prepare Firestore Data
        // Prepare data by removing password from it (if present) to avoid storing it in plaintext
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const { password: _p, ...safeUserData } = userData;

        const firestoreData = {
            ...safeUserData,
            id: uid, // Use Auth UID as document ID
            createdAt: Date.now(),
        };

        // 6. Write to Firestore (Using the MAIN app's db instance)
        // We use the main `db` because the admin is authenticated there and has permissions.
        await setDoc(doc(db, collectionName, uid), firestoreData);

        // 7. Success! Return the password to show the user
        return { password, uid };

    } catch (error: any) {
        // Handle Firebase Auth errors
        console.error("Error registering user:", error);
        if (error.code === 'auth/email-already-in-use') {
            throw new Error("This email is already registered in the system.");
        }
        if (error.code === 'auth/weak-password') {
            throw new Error("Password is too weak.");
        }
        throw new Error(error.message || "Failed to register user.");

    } finally {
        // 8. Cleanup: Sign out and delete the secondary app
        try {
            await signOut(secondaryAuth);
            await deleteApp(secondaryApp);
        } catch (cleanupError) {
            console.warn("Error cleaning up secondary app:", cleanupError);
        }
    }
};
